import React from "react";
import { Route, withRouter, Redirect, Switch } from "react-router-dom";
import styled from "styled-components";

import withAuthorization from "../components/Session/withAuthorization";

import Navigation from "../components/Navigation";
import * as routes from "../constants/routes";

import OpsPage from "../pages/Operation";
import SystemsPage from "../pages/Systems";
import OrgsPage from "../pages/Organizations";
import UsersPage from "../pages/Users";
import AccountPage from "../pages/Account";
import MessagesPage from "../pages/Messages";
import MigrationsPage from "../pages/Migrations";
import InsightsPage from "../pages/Insights";
import SysmapApi from "./SysmapApi";

import { fetchJsonData } from "../firebase/storage";
import { db } from "../firebase";

const AppLayout = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const MainContainer = styled.div`
  flex: 1;
  overflow: hidden;
  height: 100%;
`;

const DEFAULT_LANG = "en";
const ALERT_TYPES = [
  "channelStopAlert",
  "commStopAlert",
  "invStopAlert",
  "subchannelStopAlert",
];

function validateFetchedData(sysJson, sysDb, orgs, users) {
  function setOrPush(obj, key, val) {
    if (!obj[key]) obj[key] = [];

    obj[key].push(val);
  }

  const KNOWN_ROLES = ["whitelabel", "client", "viewer", "admin", "demo"];
  const KNOWN_DB_SYS_MAKE = [
    "sma",
    "solaredge",
    "fusion",
    "goodwe",
    "sungrow",
    "fronius",
    "weblog",
    "huawei",
    "ennexos",
    "zcs",
  ];
  const KNOWN_JSON_SYS_INV_MAKE = [
    "smav2",
    "goodwe",
    "solaredge",
    "solaredgeAgg",
    "sungrow",
    "nineRen",
    "fusion",
    "weblog",
    "smaApi",
    "smartPVMS",
    "mock",
  ];

  const usersErr = { errors: {}, warnings: {} };
  const orgsErr = { errors: {}, warnings: {} };
  const sysJsonErr = { errors: {}, warnings: {} };
  const sysDbErr = { errors: {}, warnings: {} };

  const parentSystems = [];

  // validate users
  for (const phone in users) {
    const user = users[phone];
    // make sure role is recognized
    if (!KNOWN_ROLES.includes(user.role)) {
      setOrPush(usersErr.warnings, phone, `unrecognized role: '${user.role}'`);
    }
    // make sure that org is defined
    if (!orgs[user.organization]) {
      setOrPush(
        usersErr.errors,
        phone,
        `undefined org: '${user.organization}'`,
      );
    }
  }

  // validate orgs
  for (const orgId in orgs) {
    const { systemIds } = orgs[orgId];
    // make sure that systemIds is a nonempty array
    if (!Array.isArray(systemIds)) {
      setOrPush(orgsErr.errors, orgId, `'systemIds' is not an array`);
    } else if (!systemIds.length) {
      setOrPush(orgsErr.warnings, orgId, `'systemIds' is empty`);
    } else {
      // systemIds is not empty, make sure that every sysId is defined
      for (const sysId of systemIds) {
        if (!sysDb[sysId]) {
          setOrPush(
            orgsErr.errors,
            orgId,
            `undefined system in rtdb: '${sysId}'`,
          );
        }
      }
    }
  }

  // validate rtdb systems
  for (const sysId in sysDb) {
    const sys = sysDb[sysId];
    let compareArrays = true;
    const isParent = !!Object.values(sysDb).find(
      (child) => child.parent_id === sysId,
    );
    if (isParent) {
      parentSystems.push(sysId);
    }

    // ensure defined id is same as key
    if (sysId !== sys.id) {
      setOrPush(
        sysDbErr.errors,
        sysId,
        `mismatch between key and defined 'id' field: '${sysId}' / '${sys.id}'`,
      );
    }
    // make sure that 'make' field is defined and recognized, if not defined only for parent systems
    if (sys.make === undefined) {
      if (!isParent) {
        setOrPush(
          sysDbErr.warnings,
          sysId,
          `'undefined' make for a non parent system`,
        );
      }
    } else if (!KNOWN_DB_SYS_MAKE.includes(sys.make)) {
      setOrPush(
        sysDbErr.warnings,
        sysId,
        `unrecognized 'make' field: '${sys.make}'`,
      );
    }
    // make sure that child systems have parents defined
    if (sys.parent_id && !sysDb[sys.parent_id]) {
      setOrPush(
        sysDbErr.errors,
        sysId,
        `'parent_id' defined without matching system: '${sys.parent_id}'`,
      );
    }
    // check subsystems and inverters for non parent systems
    if (isParent) {
      compareArrays = false;
    } else {
      let channelCounter = 0;
      if (!Array.isArray(sys.subsystems) || !sys.subsystems.length) {
        compareArrays = false;
        setOrPush(sysDbErr.errors, sysId, `has no 'subsystems'`);
      }
      if (!Array.isArray(sys.inverters) || !sys.inverters.length) {
        compareArrays = false;
        setOrPush(sysDbErr.errors, sysId, `has no 'inverters'`);
      } else {
        for (let invI = 0; invI < sys.inverters.length; ++invI) {
          const inv = sys.inverters[invI];
          if (!inv.hasOwnProperty("id")) {
            setOrPush(
              sysDbErr.errors,
              sysId,
              `inverter in index ${invI} has no 'id'`,
            );
          }
          if (!inv.hasOwnProperty("name")) {
            setOrPush(
              sysDbErr.warnings,
              sysId,
              `inverter in index ${invI} has no 'name'`,
            );
          }
          if (!Array.isArray(inv.channels) || !inv.channels.length) {
            compareArrays = false;
            setOrPush(
              sysDbErr.errors,
              sysId,
              `inverter in index ${invI} has no 'channels'`,
            );
          } else {
            for (let chanI = 0; chanI < inv.channels.length; ++chanI) {
              ++channelCounter;
              const chan = inv.channels[chanI];
              if (!chan.hasOwnProperty("id")) {
                setOrPush(
                  sysDbErr.errors,
                  sysId,
                  `channel indexed ${chanI} in inverter indexed ${invI} has no 'id'`,
                );
              }
              if (!chan.hasOwnProperty("name")) {
                setOrPush(
                  sysDbErr.warnings,
                  sysId,
                  `channel indexed ${chanI} in inverter indexed ${invI} has no 'name'`,
                );
              }
            }
          }
        }
      }
      if (compareArrays && channelCounter !== sys.subsystems.length) {
        setOrPush(
          sysDbErr.warnings,
          sysId,
          `mismatching number of channels between subsystems / inverters: ${sys.subsystems.length} / ${channelCounter}`,
        );
      }
    }
    // make sure that system also defined in sysJson file
    if (!sysJson[sysId]) {
      setOrPush(sysDbErr.errors, sysId, `no matching system in 'systems.json'`);
    } else if (compareArrays && Array.isArray(sysJson[sysId].inverterSizes)) {
      const sizes = sysJson[sysId].inverterSizes;
      const channels = sys.inverters.reduce((acc, { channels }) => {
        acc.push(true, ...Array(channels.length - 1).fill(false));
        return acc;
      }, []);

      if (sizes.length !== channels.length) {
        setOrPush(
          sysDbErr.warnings,
          sysId,
          `number of channels in 'inverters' object differs from 'inverterSizes' in 'systems.json': ${channels.length} / ${sizes.length}`,
        );
      } else {
        for (let i = 0; i < sizes.length; ++i) {
          if (channels[i] !== !!sizes[i]) {
            setOrPush(
              sysDbErr.warnings,
              sysId,
              `index ${i} of 'inverterSizes' in 'systems.json' is misconfigured in relation to 'inverters' object`,
            );
          }
        }
      }
    }
  }

  // validate systems.json
  for (const sysId in sysJson) {
    const sys = sysJson[sysId];
    // ensure defined id is same as key
    if (sysId !== sys.id) {
      setOrPush(
        sysJsonErr.errors,
        sysId,
        `mismatch between key and defined 'id' field: '${sysId}' / '${sys.id}'`,
      );
    }
    // make sure that system also defined in rtdb file
    if (!sys.is_reference && !sysDb[sysId]) {
      setOrPush(sysJsonErr.errors, sysId, `'${sysId}' has no match in rtdb`);
    }
    // check that 'inverter_make' is well defined
    if (!KNOWN_JSON_SYS_INV_MAKE.includes(sys.inverter_make)) {
      setOrPush(
        sysJsonErr.errors,
        sysId,
        `unrecognized 'inverter_make' field: '${sys.inverter_make}'`,
      );
    }
    // check that 'references' are defined and exists
    if (!sys.is_reference && !sys.reference && !parentSystems.includes(sysId)) {
      if (!Array.isArray(sys.references)) {
        setOrPush(sysJsonErr.warnings, sysId, `'references' is not an array`);
      } else if (!sys.references.length) {
        setOrPush(sysJsonErr.warnings, sysId, `empty 'references'`);
      } else {
        for (const refId of sys.references) {
          if (!sysJson[refId]) {
            setOrPush(
              sysJsonErr.warnings,
              sysId,
              `in 'references' '${refId}' is undefined`,
            );
          }
        }
      }
    }
    // check that 'inverterSizes' and 'inverterDCVEstimate' are of the same length
    if (
      Array.isArray(sys.inverterSizes) !==
      Array.isArray(sys.inverterDCVEstimate)
    ) {
      setOrPush(
        sysJsonErr.warnings,
        sysId,
        `'inverterSizes' and 'inverterDCVEstimate' are not both arrays, one is the other is not`,
      );
    } else if (
      Array.isArray(sys.inverterSizes) &&
      sys.inverterSizes.length !== sys.inverterDCVEstimate.length
    ) {
      setOrPush(
        sysJsonErr.warnings,
        sysId,
        `'inverterSizes' and 'inverterDCVEstimate' are different lengths: ${sys.inverterSizes.length} / ${sys.inverterDCVEstimate.length}`,
      );
    }
  }

  const result = Object.entries({
    sysJsonErr,
    sysDbErr,
    orgsErr,
    usersErr,
  }).reduce((acc, [errKey, errObj]) => {
    for (const problems of Object.keys(errObj)) {
      if (!Object.keys(errObj[problems]).length) delete errObj[problems];
    }

    if (Object.keys(errObj).length) acc[errKey] = errObj;

    return acc;
  }, {});

  return result;
}

// function constructor to create a new clean state with possible loading or not
function createCleanState(isLoading = false, error = null) {
  return {
    isLoading: isLoading,
    loadError: error,
    regionsData: null,
    systemsData: null,
    schedule: null,
    orgsData: null,
    usersData: null,
  };
}

class HomePage extends React.Component {
  constructor(props) {
    super(props);
    this.state = { ...createCleanState(true) };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadDataFromFirebase();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadDataFromFirebase = () => {
    this.setState(
      () => {
        if (!this._isMounted) {
          return;
        }
        return { ...createCleanState(true) };
      },
      () => {
        Promise.all([
          fetchJsonData("admin/regions.json"),
          fetchJsonData("admin/systems.json"),
          fetchJsonData("admin/schedule.json"),
          db.onceGetDB().then((res) => res.val()),
        ])
          .then(([regionsData, systemsData, schedule, dbSnapshot]) => {
            if (!this._isMounted) {
              return;
            }

            const { organizations, systems: dbSystems, users } = dbSnapshot;
            for (const sysId of Object.keys(systemsData)) {
              systemsData[sysId].orgs = [];
            }

            const systemsErrors = validateFetchedData(
              systemsData,
              dbSystems,
              organizations,
              users,
            );

            // check for missing system id's in the organizations
            const dbOrgs = Object.entries(organizations).reduce(
              (orgs, [orgId, orgData]) => {
                if (
                  !Array.isArray(orgData.systemIds) ||
                  !orgData.systemIds.length
                ) {
                  console.warn(
                    `Organization "${orgId}" misconfigured, no associated systems`,
                  );
                  window.alert(
                    `Organization "${orgId}" misconfigured, no associated systems`,
                  );
                } else {
                  for (const sysId of orgData.systemIds) {
                    if (!systemsData[sysId]) {
                      console.warn(
                        `Organization "${orgId}" misconfigured, system '${sysId}' does not exist`,
                      );
                      window.alert(
                        `Organization "${orgId}" misconfigured, system '${sysId}' does not exist`,
                      );
                    } else {
                      systemsData[sysId].orgs.push(orgId);
                    }
                  }
                  orgData.roles = {
                    admin: [],
                    client: [],
                    whitelabel: [],
                    viewer: [],
                    demo: [],
                  };
                  orgs[orgId] = orgData;
                }
                return orgs;
              },
              {},
            );

            const dbUsers = Object.entries(users).reduce(
              (usersAcc, [phone, userData]) => {
                const userOrgId = userData.organization;
                if (!dbOrgs[userOrgId]) {
                  console.warn(
                    `user "${phone}" has no valid organization '${userOrgId}'`,
                  );
                  window.alert(
                    `user "${phone}" has no valid organization '${userOrgId}'`,
                  );
                } else {
                  // basic email validation
                  const email = userData.email;
                  userData.email = email && email.includes("@") ? email : null;
                  const userRole = userData.role;
                  if (dbOrgs[userOrgId].roles[userRole]) {
                    dbOrgs[userOrgId].roles[userRole].push(phone);
                  } else {
                    console.warn(
                      `user role '${userRole}' for '${phone}' does not exist`,
                    );
                    window.alert(
                      `user role '${userRole}' for '${phone}' does not exist`,
                    );
                  }

                  // normalize alerts data
                  // if no alerts object defined, define an empty one with default language
                  if (!userData.alerts) {
                    userData.alerts = { language: DEFAULT_LANG, toSend: {} };
                  }
                  const toSend = userData.alerts.toSend;
                  // for every alert type make sure it exists for the user
                  for (const alertType of ALERT_TYPES) {
                    if (!toSend[alertType]) {
                      toSend[alertType] = {
                        isActive: false,
                        methods: { email: false, sms: false },
                      };
                    }
                    // if it's inactive, disable sending methods
                    if (!toSend[alertType].isActive) {
                      for (const key of Object.keys(
                        toSend[alertType].methods,
                      )) {
                        toSend[alertType].methods[key] = false;
                      }
                    }
                    // make sure that if email is defined as a sending method, a valid email is also defined
                    if (toSend[alertType].methods.email && !userData.email) {
                      toSend[alertType].methods.email = false;
                    }
                    // if no active method is defined, mark as inactive
                    if (
                      Object.values(toSend[alertType].methods).every((e) => !e)
                    ) {
                      toSend[alertType].isActive = false;
                    }
                  }

                  // if (
                  //   ROLES_TO_SEND.includes(userRole) &&
                  //   userData.alerts &&
                  //   userData.alerts.toSend
                  // ) {
                  //   const sysIds = dbOrgs[userOrgId].systemIds;
                  //   Object.entries(userData.alerts.toSend).forEach(
                  //     ([alertType, { isActive, methods }]) => {
                  //       if (!ALERT_TYPES.includes(alertType)) {
                  //         console.warn(
                  //           `unknown alert type '${alertType} for user '${phone}''`
                  //         );
                  //         window.alert(
                  //           `unknown alert type '${alertType} for user '${phone}''`
                  //         );
                  //       }
                  //       const sendSms = !!methods.sms;
                  //       const sendEmail = !!(userData.email && methods.email);
                  //       if (isActive && (sendSms || sendEmail)) {
                  //         sysIds.forEach((sysId) => {
                  //           const sys = systemsData[sysId];
                  //           const smsArr = sys.alerts[alertType].sms;
                  //           const emailArr = sys.alerts[alertType].email;
                  //           if (sendSms && !smsArr.includes(phone)) {
                  //             smsArr.push(phone);
                  //           }
                  //           if (
                  //             sendEmail &&
                  //             !emailArr.includes(userData.email)
                  //           ) {
                  //             emailArr.push(userData.email);
                  //           }
                  //         });
                  //       }
                  //     }
                  //   );
                  // }

                  usersAcc[phone] = userData;
                }
                return usersAcc;
              },
              {},
            );

            let missingSystem = false;

            // replace systemsData 'name', 'inverters' fields with the DB's equivalent's
            Object.keys(systemsData).forEach((id) => {
              if (systemsData[id].is_reference) {
                return;
              }
              if (!dbSystems[id]) {
                missingSystem = true;
                console.error(
                  `'${id}' is in firebase storage, and not in DB, please correct immediately`,
                );
              } else {
                // getting the flag indicating if the system is frozen for messaging purposes
                systemsData[id].frozen = !!dbSystems[id].frozen;
                if (dbSystems[id].parent_id && !systemsData[id].frozen) {
                  const parent = systemsData[dbSystems[id].parent_id];
                  systemsData[id].parent_id = parent.id;
                  if (!Array.isArray(parent.children_id)) {
                    parent.children_id = [];
                  }
                  parent.children_id.push(id);
                }
                systemsData[id].name = dbSystems[id].name
                  ? dbSystems[id].name
                  : id;
                // old style array for channels of the system
                // TODO: deprecate when all systems have moved to inverters as the next clause
                if (Array.isArray(dbSystems[id].subsystems)) {
                  systemsData[id].inverters = [...dbSystems[id].subsystems];
                }
                // new style inverter representation, takes into account system's architecture
                if (Array.isArray(dbSystems[id].inverters)) {
                  systemsData[id].inverterMapping = [
                    ...dbSystems[id].inverters,
                  ];
                }
              }
            });
            if (missingSystem) {
              window.alert(
                `problem loading systems. after this alert, press 'F12', save or capture 'console'->'errors'`,
              );
            }
            if (Object.keys(systemsErrors).length) {
              const showErrorAlert = Object.values(systemsErrors).some(
                ({ errors }) => {
                  return !!(errors && Object.keys(errors).length);
                },
              );

              if (showErrorAlert) {
                console.warn(systemsErrors);
                window.alert(
                  "problem with system configuraion, more data in console 'F12'",
                );
              } else {
                console.log(systemsErrors);
              }
            }

            this.setState(() => {
              if (!this._isMounted) {
                return;
              }
              return {
                isLoading: false,
                regionsData,
                systemsData,
                schedule,
                systemsErrors,
                orgsData: dbOrgs,
                usersData: dbUsers,
              };
            });
          })
          .catch((error) => {
            this.setState(() => {
              if (!this._isMounted) {
                return;
              }

              console.error(error);
              return { ...createCleanState(false, error) };
            });
          });
      },
    );
  };

  // rendering of the old dashboard greeting
  renderLandingDashboard = () => (
    <div>
      <h1>Dashboard</h1>
      <p>Hello there!</p>
    </div>
  );

  render() {
    const { match } = this.props;
    const {
      isLoading,
      systemsData,
      orgsData,
      usersData,
      loadError,
      systemsErrors,
    } = this.state;
    return (
      <AppLayout>
        <Navigation
          isLoadingData={isLoading}
          fetchAllData={this.loadDataFromFirebase}
        />

        <MainContainer>
          <Switch>
            <Route
              exact
              path={`${match.path}${routes.DASHBOARD}`}
              render={() => this.renderLandingDashboard()}
            />
            <Route
              exact
              path={`${match.path}${routes.ORGS}`}
              render={() => (
                <OrgsPage {...{ isLoading, loadError, orgsData }} />
              )}
            />
            <Route
              path={`${match.path}${routes.SYSTEMS}`}
              render={() => (
                <SystemsPage {...{ isLoading, loadError, systemsData }} />
              )}
            />
            <Route
              exact
              path={`${match.path}${routes.USERS}`}
              render={() => (
                <UsersPage {...{ isLoading, loadError, usersData }} />
              )}
            />
            <Route
              path={`${match.path}${routes.SYSMAP}`}
              render={() => <SysmapApi {...this.state} />}
            />
            <Route
              exact
              path={`${match.path}${routes.ACCOUNT}`}
              render={() => <AccountPage />}
            />
            <Route
              path={`${match.path}${routes.OPERATION}`}
              render={() => <OpsPage {...this.state} />}
            />
            <Route
              path={`${match.path}${routes.MESSAGES}`}
              render={() => (
                <MessagesPage
                  {...{
                    isLoading,
                    loadError,
                    systemsData,
                    orgsData,
                    usersData,
                    systemsErrors,
                  }}
                />
              )}
            />
            <Route
              path={`${match.path}${routes.MIGRATIONS}`}
              render={() => (
                <MigrationsPage {...{ isLoading, loadError, systemsData }} />
              )}
            />
            <Route
              path={`${match.path}${routes.INSIGHTS}`}
              render={() => <InsightsPage {...this.state} />}
            />
            <Redirect to={`${match.url}${routes.OPERATION}`} />
          </Switch>
        </MainContainer>
      </AppLayout>
    );
  }
}

const authCondition = (authUser) => !!authUser;

export default withAuthorization(authCondition)(withRouter(HomePage));
